import { Link } from "react-router-dom";
import { LogIn, Mail, MessageCircle, Phone } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { useSite } from "@/context/SiteContext";
import { LogoMark, Wordmark } from "@/components/Logo";
import { scrollToId } from "@/utils";
import { LOGIN_URL } from "@/config";

export const Footer = () => {
  const { t, lang, toggle, isAr } = useLang();
  const { contact, waLink } = useSite();
  const f = t.footer;

  const productLinks = [
    ["features", t.nav.features],
    ["how", t.nav.how],
    ["hardware", t.nav.hardware],
    ["ai", t.nav.ai],
  ];

  return (
    <footer className="mt-6 bg-pine text-cream">
      <div className="mx-auto max-w-7xl px-5 py-16 sm:px-8">
        <div className="grid gap-12 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <div className="flex items-center gap-2.5">
              <LogoMark className="h-9 w-9" inverted />
              <Wordmark light />
            </div>
            <p className="mt-5 max-w-sm leading-relaxed text-cream/70">{f.story}</p>
          </div>

          <div className="lg:col-span-2">
            <h4 className="text-xs font-bold uppercase tracking-[0.2em] text-cream/50">{f.product}</h4>
            <ul className="mt-4 space-y-2.5">
              {productLinks.map(([id, label]) => (
                <li key={id}>
                  <button onClick={() => scrollToId(id)} className="font-semibold text-cream/80 transition-colors hover:text-sun">
                    {label}
                  </button>
                </li>
              ))}
              <li>
                <button onClick={() => scrollToId("faq")} className="font-semibold text-cream/80 transition-colors hover:text-sun">
                  {t.nav.faq}
                </button>
              </li>
            </ul>
          </div>

          <div className="lg:col-span-2">
            <h4 className="text-xs font-bold uppercase tracking-[0.2em] text-cream/50">{f.company}</h4>
            <ul className="mt-4 space-y-2.5">
              <li>
                <button onClick={() => scrollToId("quote")} className="font-semibold text-cream/80 transition-colors hover:text-sun">
                  {t.nav.quote}
                </button>
              </li>
              <li>
                <a href={waLink(lang)} target="_blank" rel="noopener noreferrer" className="font-semibold text-cream/80 transition-colors hover:text-sun">
                  {t.nav.whatsapp}
                </a>
              </li>
              <li>
                <a href={LOGIN_URL} target="_blank" rel="noopener noreferrer" className="flex w-fit items-center gap-2 font-semibold text-cream/80 transition-colors hover:text-sun" data-testid="footer-login-link">
                  <LogIn className="h-4 w-4" />
                  {isAr ? "الدخول" : "Login"}
                </a>
              </li>
              <li>
                <Link to="/login" className="font-semibold text-cream/50 transition-colors hover:text-sun" data-testid="footer-admin-link">
                  {f.admin}
                </Link>
              </li>
            </ul>
          </div>

          <div className="lg:col-span-3">
            <h4 className="text-xs font-bold uppercase tracking-[0.2em] text-cream/50">{f.contact}</h4>
            <a href={`mailto:${contact.email}`} className="mt-4 flex items-center gap-3 font-semibold text-cream/80 transition-colors hover:text-sun">
              <Mail className="h-4 w-4" />
              <span dir="ltr">{contact.email}</span>
            </a>
            <a href={`tel:${contact.phone.replace(/\s/g, "")}`} className="mt-3 flex items-center gap-3 font-semibold text-cream/80 transition-colors hover:text-sun">
              <Phone className="h-4 w-4" />
              <span dir="ltr">{contact.phone}</span>
            </a>
            <a
              href={waLink(lang)}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-5 flex w-fit items-center gap-2 rounded-full bg-terra px-5 py-2.5 text-sm font-bold text-white transition-colors hover:bg-terra-dark"
            >
              <MessageCircle className="h-4 w-4" />
              {t.nav.whatsapp}
            </a>
          </div>
        </div>

        <div className="mt-14 flex flex-col items-start justify-between gap-4 border-t border-white/10 pt-6 sm:flex-row sm:items-center">
          <p className="text-sm text-cream/60">© {new Date().getFullYear()} itminane · {f.rights}</p>
          <button onClick={toggle} data-testid="footer-language-toggle-button" className="rounded-full border border-white/20 px-4 py-2 text-sm font-bold text-cream/80 transition-colors hover:border-sun hover:text-sun">
            {isAr ? "Switch to English" : "التبديل إلى العربية"}
          </button>
        </div>
      </div>
    </footer>
  );
};
